/**
 * Document Settings Defaults and Normalization
 * Single source of truth for document settings with validation and normalization
 */

// Default column settings (base template)
export const DEFAULT_COLUMN_SETTINGS = {
    textSize: 11,
    companyInfoTextSize: 16,
    documentTitleTextSize: 20,
    profilePictureShape: 'NoChooseShape',
    boldDate: false,
    width: 100, // mm - logo slot width
    height: 100, // mm - logo slot height (same as width for square by default)
    signatureWidth: 80, // mm - signature slot width
    signatureHeight: 40, // mm - signature slot height
    backgroundColor: '#ffffff',
    textColor: '#000000',
    upperPanelBackgroundColor: '#fbfbfb',
    svgColor: '#F9AA2A',
    svgShape: 'corner',
    upperPanelColor: '#fbfbfb',
    svgImageColor: '#F9AA2A',
    logoPosition: 'left', // 'center' or 'left'
    dateAlign: 'left', // 'left' or 'right'
    titleTextColor: '#000000',
    totalsLayout: 'horizontal', // 'horizontal' or 'vertical'
    documentFont: 'Montserrat', // font family for document body and headings
};

// Default document settings
export const DEFAULT_DOCUMENT_SETTINGS = {
    customDescription: '',
    customFooter: '',
    headerRightText: '',
    headerLeftText: '',
    itemsDisplayMode: '',
    columnSettings: DEFAULT_COLUMN_SETTINGS,
};

// Validation ranges
export const SETTINGS_RANGES = {
    textSize: { min: 8, max: 16 },
    companyInfoTextSize: { min: 12, max: 24 },
    documentTitleTextSize: { min: 16, max: 32 },
    width: { min: 40, max: 120 }, // mm
    height: { min: 40, max: 120 }, // mm
    signatureWidth: { min: 40, max: 150 }, // mm
    signatureHeight: { min: 20, max: 80 }, // mm
};

/**
 * Clamp a value to a range
 */
const clamp = (value, min, max) => {
    if (value == null) return null;
    const num = Number(value);
    if (isNaN(num)) return null;
    return Math.max(min, Math.min(max, num));
};

/**
 * Normalize and validate column settings
 * @param {Object} columnSettings - Raw column settings from API
 * @returns {Object} Normalized column settings
 */
export const normalizeColumnSettings = (columnSettings = {}) => {
    const normalized = { ...DEFAULT_COLUMN_SETTINGS };
    
    // Merge provided settings
    Object.keys(DEFAULT_COLUMN_SETTINGS).forEach(key => {
        const value = columnSettings[key];
        
        // Skip null/undefined values (use default)
        if (value == null) {
            return;
        }
        
        // Validate numeric ranges
        if (SETTINGS_RANGES[key]) {
            const clamped = clamp(value, SETTINGS_RANGES[key].min, SETTINGS_RANGES[key].max);
            if (clamped != null) {
                normalized[key] = clamped;
            }
        } else {
            // For non-numeric values, use as-is if valid
            normalized[key] = value;
        }
    });
    
    // Ensure width and height are valid numbers
    if (normalized.width == null || normalized.width <= 0) {
        normalized.width = DEFAULT_COLUMN_SETTINGS.width;
    }
    if (normalized.height == null || normalized.height <= 0) {
        normalized.height = DEFAULT_COLUMN_SETTINGS.height;
    }
    
    return normalized;
};

/**
 * Normalize full document settings
 * @param {Object} settings - Raw settings from API
 * @returns {Object} Normalized settings
 */
export const normalizeDocumentSettings = (settings = {}) => {
    return {
        customDescription: settings.customDescription || settings.constantClientDescription || '',
        customFooter: settings.customFooter || settings.constantInvoiceDescription || '',
        headerRightText: settings.headerRightText || '',
        headerLeftText: settings.headerLeftText || '',
        itemsDisplayMode: settings.itemsDisplayMode || '',
        columnSettings: normalizeColumnSettings(settings.columnSettings),
    };
};

/**
 * Convert mm to px for screen preview (approximate: 1mm ≈ 3.7795px at 96dpi)
 * For more accurate conversion, use: mm * (96 / 25.4)
 */
export const mmToPx = (mm) => {
    if (!mm || mm <= 0) return 0;
    return mm * (96 / 25.4);
};

/**
 * Get logo slot dimensions in px for preview
 * @param {Object} columnSettings - Column settings
 * @returns {Object} { widthPx, heightPx }
 */
export const getLogoSlotDimensions = (columnSettings) => {
    const widthMm = columnSettings?.width || DEFAULT_COLUMN_SETTINGS.width;
    const heightMm = columnSettings?.height || DEFAULT_COLUMN_SETTINGS.height;
    
    // Convert mm to px for screen preview
    // Use larger multiplier so logo appears prominent and fills container
    // The image will maintain its original aspect ratio via object-fit: contain
    const MAX_SLOT_WIDTH = 250; // px - allow larger logos
    const MAX_SLOT_HEIGHT = 140; // px (must fit in 150px header with padding)
    
    // Scale based on slider value: larger multiplier for more visible logo
    // When slider = 40mm → ~96px, when slider = 120mm → 250px (capped)
    const isRelativeSizing = widthMm === heightMm;
    let widthPx = widthMm * 2.0; // Larger multiplier: slider value directly affects size
    let heightPx = isRelativeSizing 
        ? heightMm * 2.0  // Same ratio for square sizing
        : heightMm * 1.5; // Slightly smaller for non-relative
    
    // Cap maximum dimensions to prevent overflow from header
    if (widthPx > MAX_SLOT_WIDTH) {
        widthPx = MAX_SLOT_WIDTH;
    }
    if (heightPx > MAX_SLOT_HEIGHT) {
        heightPx = MAX_SLOT_HEIGHT;
    }
    
    return {
        widthPx: Math.round(widthPx),
        heightPx: Math.round(heightPx),
        widthMm,
        heightMm,
    };
};

/**
 * Get object-fit value based on settings and shape
 * Use 'contain' to show full logo without cropping for professional appearance
 * @param {Object} columnSettings - Column settings
 * @returns {string} 'contain'
 */
export const getObjectFit = (columnSettings) => {
    // Always use 'contain' to show the full logo without cutting off parts
    // The logo will be scaled to fit within the container while maintaining aspect ratio
    // This ensures the complete logo is visible and looks professional
    return 'contain';
};

/**
 * Get signature slot dimensions in px for preview
 * @param {Object} columnSettings - Column settings
 * @returns {Object} { widthPx, heightPx }
 */
export const getSignatureSlotDimensions = (columnSettings) => {
    const widthMm = columnSettings?.signatureWidth || DEFAULT_COLUMN_SETTINGS.signatureWidth;
    const heightMm = columnSettings?.signatureHeight || DEFAULT_COLUMN_SETTINGS.signatureHeight;
    
    // Convert to px for screen preview
    const MAX_SIGNATURE_WIDTH = 300; // px
    const MAX_SIGNATURE_HEIGHT = 150; // px
    
    // Use multiplier for signature size
    let widthPx = widthMm * 2.0;
    let heightPx = heightMm * 2.0;
    
    // Cap maximum dimensions
    if (widthPx > MAX_SIGNATURE_WIDTH) {
        widthPx = MAX_SIGNATURE_WIDTH;
    }
    if (heightPx > MAX_SIGNATURE_HEIGHT) {
        heightPx = MAX_SIGNATURE_HEIGHT;
    }
    
    return {
        widthPx: Math.round(widthPx),
        heightPx: Math.round(heightPx),
        widthMm,
        heightMm,
    };
};

